import * as Sentry from "@sentry/vue";
import type { WalletDto } from "@umi/api/schemas/wallet";
import { CHAINS, getChain, getChainManager, type ChainArch, type ChainTag } from "@umi/shared/chains";
import type { Token } from "@umi/shared/types/token";
import type { HydratedWallet } from "@umi/shared/types/wallet";
import { createWallet, hashWallet } from "@/utils/wallets";
import _ from "lodash";
import { defineStore } from "pinia";
import type { Address } from "viem";
import { useAuthStore } from "./auth.store";



interface State {
  synchronizing: boolean;
  addressMap: Record<string, HydratedWallet>;
  hashMap:    Record<string, HydratedWallet>;
  tokensMap:  Record<ChainTag, Record<string, Token | undefined>>;
}

export const useWalletStore = defineStore("wallet", {

  state: (): State => ({
    synchronizing: false,

    ...((() => {
      const hydratedWallets = hydrateWallets([...LocalStorage.wallets.value]);
      return {
        addressMap: hydratedWallets.reduce((map, wallet) => {
          map[wallet.address] = wallet;
          return map;
        }, {} as Record<string, HydratedWallet>),
        hashMap: hydratedWallets.reduce((map, wallet) => {
          map[wallet.syncHash] = wallet;
          return map;
        }, {} as Record<string, HydratedWallet>),
      };
    })()),

    tokensMap: CHAINS.reduce((map, chain) => {
      const nativeToken = chain.token;
      map[chain.tag] = { [nativeToken.address]: nativeToken };
      return map;
    }, {} as Record<ChainTag, Record<string, Token>>)

  }),


  getters: {
    wallets: (state) => (chainArch?: ChainArch) => {
      return _.values(state.addressMap).filter(w => !chainArch || w.chainArch === chainArch);
    },
    wallet: (state) => (address: string) => state.addressMap[address] ?? null,
    token: (state) => (chainTag: ChainTag, tokenAddress?: string) => {
      const address = tokenAddress || getChain(chainTag).token.address;
      return state.tokensMap[chainTag]?.[address];
    },
  },


  actions: {


    async loadToken(chainTag: ChainTag, address: string) {
      const chain = getChain(chainTag);
      const cachedToken = this.tokensMap[chainTag][address];

      if (cachedToken) return cachedToken; // Return cached token if available

      if (chain.arch === "EVM") {
        const evm = getChainManager(chain.tag);
        const token = await evm.getToken(address as Address);
        if (!token) return null;
        this.tokensMap[chainTag][address] = token;
        return token;
      }
      return null;
    },


    async fetchBalances({
      chainTags,
      explicitWallets,
      chainArch,
      tokenAddress,
    }: {
      chainTags: ChainTag[],
      explicitWallets?: HydratedWallet[]
      chainArch?: ChainArch,
      tokenAddress?: string,
    }) {
      const wallets = explicitWallets?.length ? explicitWallets : this.wallets(chainArch);
      const results = await Promise.allSettled(
        wallets.flatMap((wallet) =>
          chainTags.map((chainTag) => wallet.refreshBalance(chainTag, tokenAddress))
        )
      );

      const failed = results.filter(result => result.status === "rejected").length;
      return { failed, total: results.length };
    },


    // Wait for the wallets to be synchronized with the server.
    async waitForSync() {
      while (this.synchronizing) {
        await new Promise(resolve => { setTimeout(resolve, 100); });
      }
    },


    // Reconcile wallet list between the local storage and the server.
    async reconcileWallets() {
      try {

        // Wait for an ongoing sync to complete.
        await this.waitForSync();

        const api = useORPC();

        this.synchronizing = true;

        const { additions, removals } = await api.wallet.reconcile({
          localHashes: _.map(this.wallets(), "syncHash"),
        });

        removals.forEach(this.deleteWalletWithHashLocally);
        this.addAndHydrateWalletsLocally(additions);
        this.syncLocalStorage();

      } catch (err) {
        if (isUnauthorizedError(err)) return;
        showErrorNotification("Failed to synchronize wallets, open a support ticket.");
        console.error(err);
        const authStore = useAuthStore();
        Sentry.captureException(err, { extra: { user: authStore.user } });
      } finally {
        this.synchronizing = false;
      }
    },


    // Store the wallets in the localstorage.
    // Needed because some changes require that we don"t
    // immediately store it in the localstorage.
    syncLocalStorage() {
      LocalStorage.wallets.value = _.values(this.addressMap);
    },


    // Update a wallet in the store. Will NOT update the wallet
    // on the server and will NOT update the localstorage.
    updateWalletLocally(wallet: HydratedWallet) {
      delete this.hashMap[wallet.syncHash]; // Remove the old hash
      wallet.syncHash = hashWallet(wallet);
      this.addressMap[wallet.address] = wallet;
      this.hashMap[wallet.syncHash]   = wallet;
    },


    // Delete a wallet from the store, will NOT delete the wallet
    // on the server and will NOT delete the wallet from the localstorage.
    deleteWalletWithHashLocally(walletHash: string) {
      const wallet = this.hashMap[walletHash];
      if (!wallet) return;
      delete this.addressMap[wallet.address];
      delete this.hashMap[walletHash];
    },


    // Add wallets to the store, will NOT add the wallets
    // on the server and will NOT add the wallets to the localstorage.
    addAndHydrateWalletsLocally(addedWallets: WalletDto[]) {
      hydrateWallets(addedWallets).forEach(wallet => {
        wallet.syncHash = hashWallet(wallet);
        this.addressMap[wallet.address] = wallet;
        this.hashMap[wallet.syncHash] = wallet;
      });
    },


    // Export the given wallets as a CSV file.
    async exportWalletsToCsvString(exportedWallets: HydratedWallet[]) {

      const decryptedPks = await decryptPks(exportedWallets);

      return jsonToCsv(exportedWallets.map((wallet) => {
        return {
          Name: wallet.name,
          Address: wallet.address,
          addedAt: wallet.addedAt,
          ...(() => {
            if (wallet.type === "internal") {
              const decrypted = decryptedPks[wallet.address];
              if (!decrypted) throw new Error(`Missing decrypted key for ${wallet.address}`);
              return {
                Imported: wallet.imported ? "Yes" : "No",
                "Private Key": decrypted.pk,
              };
            }
          })(),
        };
      }));
    },


    // Generate new wallets and save them locally and on the server.
    async createWallets<A extends ChainArch>(chainArch: A, count: number, tags: string[] = []) {

      const [wallets] = await Promise.all([
        createWalletsWithWorker(chainArch, count, tags),
        this.waitForSync(),
      ]);

      const [error, , isDefined] = await useSafeORPC().wallet.add({ newWallets: wallets });
      if (error) {
        if (isDefined && error.code === "MAX_WALLETS_REACHED") {
          const { maximum, current } = error.data;
          showErrorNotification(`Wallet limit reached (${current} of ${maximum}).`);
        } else {
          showErrorNotification("Failed to create wallets. Open a support ticket.");
        }
        throw error;
      }

      this.addAndHydrateWalletsLocally(wallets);
      this.syncLocalStorage();
    },


    // Import wallets and save them locally and on the server.
    async importWallets<A extends ChainArch>(
      chainArch: A,
      items: {
        name: string,
        tags: string[],
        pk: string
      }[]
    ) {

      await this.waitForSync();

      const wallets = await Promise.all(
        items.map(async ({ name, tags, pk }) => {
          return await createWallet(
            {
              chainArch,
              pk,
              name,
              tags,
              imported: true,
            },
            (pk) => encrypt(pk)
          );
        })
      );

      const [error, , isDefined] = await useSafeORPC().wallet.add({ newWallets: wallets });
      if (error) {
        if (isDefined && error.code === "MAX_WALLETS_REACHED") {
          const { maximum, current } = error.data;
          showErrorNotification(`Wallet limit reached (${current} of ${maximum}).`);
        } else {
          showErrorNotification("Failed to import wallets. Open a support ticket.");
        }
        throw error;
      }

      this.addAndHydrateWalletsLocally(wallets);
      this.syncLocalStorage();
    },


    // Update multiple wallets locally and on the server.
    async updateWallets(updatedWallets: HydratedWallet[]) {
      updatedWallets.forEach(w => this.updateWalletLocally(w));
      this.syncLocalStorage();

      const api = useORPC();
      await api.wallet.update({
        updates: updatedWallets.map(w => ({
          address: w.address,
          name: w.name,
          tags: w.tags,
          syncHash: w.syncHash,
        }))
      });
    },


    // Remove a wallet from the localstorage and the server.
    async deleteWallets(removedWallets: HydratedWallet[]) {
      await this.waitForSync();
      const api = useORPC();
      await api.wallet.delete({ addresses: _.map(removedWallets, "address") });
      for (const wallet of removedWallets) {
        this.deleteWalletWithHashLocally(wallet.syncHash);
      }
      this.syncLocalStorage();
    },


  },
});


if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useWalletStore, import.meta.hot))
}
